import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createBlock as _createBlock, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "mb-3 mr-3 flex items-center gap-2" }
const _hoisted_2 = {
  key: 0,
  class: "text-xs bg-red-500 text-white rounded-full px-1 py-0.5 absolute flex items-center justify-center",
  style: {"top":"-7px","right":"-7px","min-width":"20px"}
}
const _hoisted_3 = {
  key: 0,
  class: "text-sm flex flex-col items-center justify-center gap-1 select-none flex-1 text-center"
}
const _hoisted_4 = {
  key: 1,
  class: "text-sm text-center flex-1 flex items-center justify-center"
}
const _hoisted_5 = {
  key: 2,
  class: "flex flex-col gap-2 justify-center relative overflow-hidden",
  "data-oai-left-over-space-calculator": "ignore"
}
const _hoisted_6 = {
  class: "text-white-300 text-xs truncate",
  style: {"font-size":"12px"}
}
const _hoisted_7 = {
  key: 3,
  class: "flex items-center gap-3 mt-3"
}
const _hoisted_8 = {
  class: "text-gray-500 text-xs truncate",
  style: {"font-size":"12px"}
}
const _hoisted_9 = { key: 0 }

import { ChevronRightIcon, CheckCircleIcon } from "@heroicons/vue/24/outline";
import { useCurrentCustomerName, useCurrentSiteId } from "@oculai/planner";
import { format, intervalToDuration } from "date-fns";
import { computed, onMounted, ref } from "vue";
import { segmentAnalytics } from "@/assets/scripts";
import { useLiveImageSetting } from "@/composables/stream";
import OpsProcessesRepository from "@/repositories/OpsProcessesRepository";
import logger from "@/services/logger";
import { LastSeenProcessResult, Process } from "@/types/Process";
import BaseWidget from "@/views/dashboard/componentsV2/BaseWidget.vue";
import DashboardDragHandle from "@/views/dashboard/componentsV2/DashboardDragHandle.vue";
import WidgetVideoPlayer from "@/views/dashboard/componentsV2/WidgetVideoPlayer.vue";
import { normalWidgetWidth, useVideoPlayerDimensions } from "@/views/dashboard/composables";


export default /*#__PURE__*/_defineComponent({
  __name: 'ProcessWidget',
  props: {
    width: {},
    height: {}
  },
  emits: ["delete"],
  setup(__props: any) {




const lastSeenResult = ref<LastSeenProcessResult | null>(null);
const isInitialLoad = ref(true);
const isLoading = ref(false);
const showOverlay = ref(true);

const currentCustomerName = useCurrentCustomerName();
const currentSiteId = useCurrentSiteId();

const containerRef = ref<HTMLDivElement | null>(null);
const getContainerRef = () => containerRef;

const videoPlayerDimensions = useVideoPlayerDimensions(containerRef);

const load = () => {
  isLoading.value = true;
  return OpsProcessesRepository.loadLastSeenProcess(currentCustomerName, currentSiteId)
    .then((data) => {
      lastSeenResult.value = data;
    })
    .catch((error) => {
      logger.error(error);
      lastSeenResult.value = null;
    })
    .finally(() => {
      isInitialLoad.value = false;
      isLoading.value = false;
    });
};

const loadNext = () => {
  isLoading.value = true;
  return OpsProcessesRepository.loadNextLastSeenProcess(
    currentCustomerName,
    currentSiteId,
    ((lastSeenResult.value as LastSeenProcessResult).process as Process).process_id,
  )
    .then((data) => {
      lastSeenResult.value = data;
      if (!data.process) {
        segmentAnalytics("process_widget_all_caught_up");
      }
    })
    .catch((error) => {
      logger.error(error);
      lastSeenResult.value = null;
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const handleNextClick = () => {
  segmentAnalytics("process_widget_next_click");
  loadNext();
};

const getFormattedDuration = (startTime: Date, endTime: Date) => {
  const duration = intervalToDuration({
    start: endTime,
    end: startTime,
  });
  const durationFormattedHours = (duration.hours || 0).toString().padStart(2, "0");
  const durationFormattedMinutes = (duration.minutes || 0).toString().padStart(2, "0");

  return `${durationFormattedHours}:${durationFormattedMinutes}h`;
};

const location = computed(() =>
  [
    lastSeenResult.value?.process?.section_mask_mapping.building_name,
    lastSeenResult.value?.process?.section_mask_mapping.section_name,
    lastSeenResult.value?.process?.section_mask_mapping.level_name,
  ]
    .filter((item) => item)
    .join(" / "),
);

onMounted(() => {
  load();
});

const { useLiveImage } = useLiveImageSetting();

return (_ctx: any,_cache: any) => {
  const _component_RouterLink = _resolveComponent("RouterLink")!
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createBlock(BaseWidget, {
    isLoading: isInitialLoad.value && isLoading.value,
    onDelete: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('delete'))),
    hideDragHandle: ""
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", {
        class: "h-full flex flex-col px-4 py-3 overflow-auto",
        ref_key: "containerRef",
        ref: containerRef,
        onMouseenter: _cache[2] || (_cache[2] = ($event: any) => (showOverlay.value = false))
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createVNode(DashboardDragHandle, { class: "shrink-0" }),
          _createVNode(_component_RouterLink, {
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(segmentAnalytics)('process_widget_detail_click'))),
            class: _normalizeClass(["font-semibold text-gray-900 truncate hover:text-yellow-800", _ctx.width > _unref(normalWidgetWidth) ? 'text-lg' : 'text-base']),
            to: {
            name: 'ProcessesTable',
            params: {
              customer_name: _unref(currentCustomerName),
              site_id: _unref(currentSiteId),
            },
          }
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t("dashboard.process_widget.header")), 1)
            ]),
            _: 1
          }, 8, ["class", "to"]),
          (lastSeenResult.value?.process && lastSeenResult.value?.not_seen_count !== null)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(lastSeenResult.value.not_seen_count), 1))
            : _createCommentVNode("", true)
        ]),
        (lastSeenResult.value && !lastSeenResult.value.process && lastSeenResult.value?.process_count > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              (_ctx.width > _unref(normalWidgetWidth))
                ? (_openBlock(), _createBlock(_unref(CheckCircleIcon), {
                    key: 0,
                    class: "h-16 w-16 text-green-400"
                  }))
                : _createCommentVNode("", true),
              _createElementVNode("span", null, _toDisplayString(_ctx.$t("dashboard.process_widget.all_caught_up")), 1),
              _createVNode(_component_router_link, {
                to: { name: 'ProcessesTable' },
                class: "text-gray-600 underline"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t("dashboard.process_widget.to_overview_link")), 1)
                ]),
                _: 1
              })
            ]))
          : _createCommentVNode("", true),
        (!lastSeenResult.value || (!lastSeenResult.value.process && lastSeenResult.value.process_count === 0))
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(_ctx.$t("dashboard.process_widget.no_processes")), 1))
          : _createCommentVNode("", true),
        (lastSeenResult.value?.process)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(WidgetVideoPlayer, {
                getContainerRef: getContainerRef,
                loading: isLoading.value,
                src: lastSeenResult.value?.url,
                autoPlay: !_unref(useLiveImage)
              }, null, 8, ["loading", "src", "autoPlay"]),
              (lastSeenResult.value?.process && showOverlay.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: "absolute text-white text-center bg-yellow-500 py-3 px-3 whitespace-nowrap opacity-90 top-1/2 left-1/2",
                    style: _normalizeStyle({
            transform: `translate(-50%, -50%)`,
            width: `${_unref(videoPlayerDimensions).width}px`,
          })
                  }, [
                    _createElementVNode("div", {
                      class: _normalizeClass(["font-semibold truncate", _ctx.width > _unref(normalWidgetWidth) ? 'text-base' : 'text-sm'])
                    }, _toDisplayString(_ctx.$t(
                `report.sections.processes.process_type_entries.${lastSeenResult.value?.process.encoded_label}`,
              )), 3),
                    _createElementVNode("div", _hoisted_6, _toDisplayString(getFormattedDuration(
                lastSeenResult.value?.process.start_time,
                lastSeenResult.value?.process.end_time,
              )) + " (" + _toDisplayString(_unref(format)(lastSeenResult.value?.process.start_time, "dd.MM.yyyy")) + ") ", 1)
                  ], 4))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true),
        (lastSeenResult.value?.process)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              _createVNode(_component_router_link, {
                onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(segmentAnalytics)('process_widget_detail_click'))),
                to: {
            name: 'ProcessesTable',
            params: {
              customer_name: _unref(currentCustomerName),
              site_id: _unref(currentSiteId),
            },
            query: {
              processId: lastSeenResult.value?.process?._id,
            },
          },
                class: "hover:!text-orange-700 flex-1 overflow-hidden"
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", {
                    class: _normalizeClass(["font-bold truncate", _ctx.width > _unref(normalWidgetWidth) ? 'text-base' : 'text-sm'])
                  }, _toDisplayString(_ctx.$t(
                `report.sections.processes.process_type_entries.${lastSeenResult.value?.process.encoded_label}`,
              )), 3),
                  _createElementVNode("div", {
                    class: _normalizeClass(["truncate", _ctx.width > _unref(normalWidgetWidth) ? 'text-sm' : 'text-xs'])
                  }, _toDisplayString(getFormattedDuration(
                lastSeenResult.value?.process.start_time,
                lastSeenResult.value?.process.end_time,
              )) + " (" + _toDisplayString(_unref(format)(lastSeenResult.value?.process.start_time, "dd.MM.yyyy")) + ") ", 3),
                  _createElementVNode("div", _hoisted_8, _toDisplayString(location.value), 1)
                ]),
                _: 1
              }, 8, ["to"]),
              _createElementVNode("button", {
                type: "button",
                class: _normalizeClass(["flex items-center gap-1 bg-orange-300 text-white rounded pr-2 py-1 text-xs w-min", _ctx.width > _unref(normalWidgetWidth) ? 'pl-3' : 'pl-2']),
                onClick: handleNextClick
              }, [
                (_ctx.width > _unref(normalWidgetWidth))
                  ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(_ctx.$t("dashboard.process_widget.next_button")), 1))
                  : _createCommentVNode("", true),
                _createVNode(_unref(ChevronRightIcon), { class: "w-5 h-5 cursor-pointer select-none" })
              ], 2)
            ]))
          : _createCommentVNode("", true)
      ], 544)
    ]),
    _: 1
  }, 8, ["isLoading"]))
}
}

})